import * as React from 'react'
import { useState } from 'react'
import { 
  type MVSErgebnis, 
  Zahlungsintervall 
} from '../../types/mvs'

interface SteuerVergleichTabelleProps {
  steuerAlt: MVSErgebnis
  steuerNeu: MVSErgebnis
  sindZuschlaegeFuerZahlungsintervallAnwendbar?: boolean
  className?: string
}

// Zahlungsintervall Info für Darstellung
interface ZahlungsintervallInfo {
  id: Zahlungsintervall
  label: string
  isHighlighted?: boolean
}

// Tabellenzeile für besser strukturierte Komponente
interface ZeilenProps {
  intervallInfo: ZahlungsintervallInfo
  steuerAlt: MVSErgebnis
  steuerNeu: MVSErgebnis
  sindZuschlaegeFuerZahlungsintervallAnwendbar: boolean
  formatCurrency: (amount: number) => string
}

// Wiederverwendbare Tabellenzeile
function ZahlungsweiseZeile({ 
  intervallInfo, 
  steuerAlt, 
  steuerNeu,
  sindZuschlaegeFuerZahlungsintervallAnwendbar,
  formatCurrency 
}: ZeilenProps) {
  const { id, label, isHighlighted } = intervallInfo
  
  // Berechnung der Werte für dieses Zahlungsintervall
  const monate = getMonateFuerZahlungsintervall(id)
  
  // Zuschlagsfaktor nur anwenden, wenn es zulässig ist
  const faktor = sindZuschlaegeFuerZahlungsintervallAnwendbar ? 
    getZahlungsintervallFaktor(id) : 1.0
  
  const betragAlt = steuerAlt.monatlicheSteuer * monate * faktor
  const betragNeu = steuerNeu.monatlicheSteuer * monate * faktor
  const differenz = betragNeu - betragAlt
  
  const prozent = betragAlt === 0 
    ? (betragNeu === 0 ? 0 : 100) 
    : (differenz / betragAlt) * 100
  
  // Styling für bessere Lesbarkeit
  const rowClass = isHighlighted 
    ? 'bg-orange-100 font-medium' 
    : ''
  
  const diffClass = differenz === 0
    ? 'text-gray-500'
    : differenz > 0
      ? 'text-red-600' 
      : 'text-green-600'
  
  return (
    <tr className={rowClass}>
      <td className="py-2 px-3 border border-orange-200 whitespace-nowrap">{label}</td>
      <td className="py-2 px-3 border border-orange-200 text-right whitespace-nowrap">{formatCurrency(betragAlt)}</td>
      <td className="py-2 px-3 border border-orange-200 text-right whitespace-nowrap">{formatCurrency(betragNeu)}</td>
      <td className={`py-2 px-3 border border-orange-200 text-right whitespace-nowrap ${diffClass}`}>
        {differenz === 0 ? '-' : formatCurrency(differenz)}
      </td>
    </tr>
  )
}

// Hauptkomponente für die Vergleichstabelle
function SteuerVergleichTabelle({
  steuerAlt,
  steuerNeu,
  sindZuschlaegeFuerZahlungsintervallAnwendbar = true,
  className = ''
}: SteuerVergleichTabelleProps) {
  // Formatiert Währungsbeträge
  function formatCurrency(amount: number) {
    return new Intl.NumberFormat('de-AT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
  }

  // Zahlungsintervalle für die Tabelle
  const intervalle: ZahlungsintervallInfo[] = [
    { id: Zahlungsintervall.MONATLICH, label: 'Monatlich' },
    { id: Zahlungsintervall.VIERTELJAEHRLICH, label: 'Vierteljährlich' },
    { id: Zahlungsintervall.HALBJAEHRLICH, label: 'Halbjährlich' },
    { id: Zahlungsintervall.JAEHRLICH, label: 'Jährlich', isHighlighted: true }
  ]

  // Berechnung der Differenz und der prozentualen Änderung für die Jahreswerte
  const differenz = steuerNeu.steuerJaehrlich - steuerAlt.steuerJaehrlich
  const prozentAnderung = steuerAlt.steuerJaehrlich === 0 
    ? (steuerNeu.steuerJaehrlich === 0 ? 0 : 100) 
    : (differenz / steuerAlt.steuerJaehrlich) * 100

  // Status der Änderung
  const istErhohung = differenz > 0
  const istVerringerung = differenz < 0
  const hatKeineAnderung = differenz === 0

  return (
    <div className={`${className}`}>
      <h3 className="text-lg font-medium mb-4">Vergleich der Steuerbeträge</h3>
      
      <div className="overflow-x-auto">
        <table className="w-full bg-white border-collapse">
          <thead>
            <tr className="bg-orange-200">
              <th className="py-2 px-3 border border-orange-300 text-left text-xs font-medium text-orange-900 uppercase tracking-wider" style={{ minWidth: '120px' }}>
                Zahlungsweise
              </th>
              <th className="py-2 px-3 border border-orange-300 text-right text-xs font-medium text-orange-900 uppercase tracking-wider" style={{ minWidth: '110px' }}>
                Alte Regelung
              </th>
              <th className="py-2 px-3 border border-orange-300 text-right text-xs font-medium text-orange-900 uppercase tracking-wider" style={{ minWidth: '110px' }}>
                Neue Regelung
              </th>
              <th className="py-2 px-3 border border-orange-300 text-right text-xs font-medium text-orange-900 uppercase tracking-wider" style={{ minWidth: '110px' }}>
                Differenz
              </th>
            </tr>
          </thead>
          <tbody>
            {intervalle.map(intervall => (
              <ZahlungsweiseZeile
                key={intervall.id}
                intervallInfo={intervall}
                steuerAlt={steuerAlt}
                steuerNeu={steuerNeu}
                sindZuschlaegeFuerZahlungsintervallAnwendbar={sindZuschlaegeFuerZahlungsintervallAnwendbar}
                formatCurrency={formatCurrency}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// Helfer-Funktionen für Berechnungen
function getMonateFuerZahlungsintervall(zahlungsintervall: Zahlungsintervall): number {
  switch (zahlungsintervall) {
    case Zahlungsintervall.JAEHRLICH:
      return 12
    case Zahlungsintervall.HALBJAEHRLICH:
      return 6
    case Zahlungsintervall.VIERTELJAEHRLICH:
      return 3
    case Zahlungsintervall.MONATLICH:
    default:
      return 1
  }
}

function getZahlungsintervallFaktor(zahlungsintervall: Zahlungsintervall): number {
  switch (zahlungsintervall) {
    case Zahlungsintervall.HALBJAEHRLICH:
      return 1.06 // +6%
    case Zahlungsintervall.VIERTELJAEHRLICH:
      return 1.08 // +8%
    case Zahlungsintervall.MONATLICH:
      return 1.10 // +10%
    case Zahlungsintervall.JAEHRLICH:
    default:
      return 1.0 // kein Zuschlag
  }
}

export { SteuerVergleichTabelle }